// src/services/certificateService.ts
import apiClient from '@/api/apiClient';
import { ID } from '@/types';
import {
    Certificate,
    CertificateApplication,
    CertificateTemplate,
    CertificateVerification,
    LicenseStats,
    CertificateType,
    ApplicationType,
    APIResponse,
    ExternalRegistrationResult,
    SupportedAssociation,
    ApplicationSearchFilters,
    CertificateSearchFilters
} from '@/types/certificate';

import { canViewAllCertificates, getUserIdFromToken, isAuthenticated } from '@/utils/authHelper.ts'; // ✅ Import helper

class CertificateService {
    // ===== APPLICATION METHODS =====

    async createApplication(applicationData: any): Promise<CertificateApplication> {
        try {
            console.log('Creating application with data:', applicationData);

            // Lấy userId từ localStorage
            const userId = getUserIdFromToken();

            // Prepare data according to Strapi schema
            const formattedData: any = {
                // Required fields
                applicationNumber: this.generateApplicationNumber(),
                applicationType: applicationData.applicationType || 'new',
                applicationDate: new Date().toISOString(),
                applicationStatus: 'draft', // ✅ Luôn bắt đầu với draft
                targetCertificateType: applicationData.certificateType,
                applicationData: applicationData.applicationData || {},

                // ✅ FIX: THÊM applicant field
                applicant: userId ? parseInt(userId) : null,

                // Optional fields
                priority: applicationData.priority || 'normal',
                paymentStatus: 'unpaid',
                processingFee: this.calculateProcessingFee(
                    applicationData.certificateType,
                    applicationData.applicationType || 'new',
                    applicationData.priority || 'normal'
                ),
                estimatedProcessingTime: this.getEstimatedProcessingTime(
                    applicationData.certificateType,
                    applicationData.priority || 'normal'
                )
            };

            // Handle content relation if provided
            if (applicationData.contentId) {
                formattedData.content = applicationData.contentId;
            }

            // Remove undefined fields
            Object.keys(formattedData).forEach(key => {
                if (formattedData[key] === undefined) {
                    delete formattedData[key];
                }
            });

            console.log('Formatted data for Strapi:', formattedData);

            const response = await apiClient.post('/api/certificate-applications', {
                data: formattedData
            });

            console.log('Application created successfully:', response.data);

            // ✅ Trả về application (draft), user sẽ phải submit riêng
            return response.data.data;

        } catch (error: any) {
            console.error('Error creating application:', error);

            // Log detailed error
            if (error.response?.data?.error) {
                console.error('Strapi error:', error.response.data.error);
            }

            throw error;
        }
    }

    async getUserApplications(params: {
        page?: number;
        pageSize?: number;
        filters?: ApplicationSearchFilters;
    } = {}): Promise<APIResponse<CertificateApplication[]>> {
        try {
            console.log('🔍 Getting user applications with params:', params);

            const queryParams = new URLSearchParams();
            // ✅ Sử dụng helper function thay vì logic riêng
            const userId = getUserIdFromToken();

            // ✅ SỬ DỤNG UTILITY FUNCTION
            const canViewAll = canViewAllCertificates();

            // User filter
            if (canViewAll) {
                // Admin/Staff/Legal: Xem tất cả applications (không filter by user)
                // Có thể thêm filter khác như status, priority...
            } else {
                // Right_owner và các role khác: Chỉ xem applications của mình
                queryParams.append('filters[applicant][$eq]', userId);
            }



            // Basic params
            if (params.page) queryParams.append('pagination[page]', params.page.toString());
            if (params.pageSize) queryParams.append('pagination[pageSize]', params.pageSize.toString());

            // // User filter
            // queryParams.append('filters[applicant][$eq]', userId);

            // ✅ Certificate type filter
            if (params.filters?.targetCertificateType) {
                queryParams.append('filters[targetCertificateType][$eq]', params.filters.certificateType);
            }
            if (params.filters?.targetCertificateType) {
                queryParams.append('filters[targetCertificateType][$eq]', params.filters.targetCertificateType);
            }

            // Status filters
            if (params.filters?.status && params.filters.status.length > 0) {
                params.filters.status.forEach((status, index) => {
                    queryParams.append(`filters[applicationStatus][$in][${index}]`, status);
                });
            }

            // Type filters  
            if (params.filters?.type && params.filters.type.length > 0) {
                params.filters.type.forEach((type, index) => {
                    queryParams.append(`filters[applicationType][$in][${index}]`, type);
                });
            }

            // Priority filters
            if (params.filters?.priority && params.filters.priority.length > 0) {
                params.filters.priority.forEach((priority, index) => {
                    queryParams.append(`filters[priority][$in][${index}]`, priority);
                });
            }

            // Keywords search
            if (params.filters?.keywords) {
                queryParams.append('filters[$or][0][applicationNumber][$containsi]', params.filters.keywords);
                queryParams.append('filters[$or][1][applicationData.workTitle][$containsi]', params.filters.keywords);
            }

            // Date range
            if (params.filters?.dateRange) {
                if (params.filters.dateRange.from) {
                    queryParams.append('filters[applicationDate][$gte]', params.filters.dateRange.from);
                }
                if (params.filters.dateRange.to) {
                    queryParams.append('filters[applicationDate][$lte]', params.filters.dateRange.to);
                }
            }

            // Populate related data
            queryParams.append('populate[0]', 'applicant');
            queryParams.append('populate[1]', 'content');
            queryParams.append('populate[2]', 'reviewedBy');
            queryParams.append('populate[3]', 'supportingDocuments');

            // Sort
            queryParams.append('sort[0]', 'createdAt:desc');

            const url = `/api/certificate-applications?${queryParams.toString()}`;
            console.log('🌐 User Applications API URL:', url);

            const response = await apiClient.get(url);
            console.log('📊 User Applications Response:', response.data);

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ Error fetching user applications:', error);
            throw error;
        }
    }

    async getApplicationById(id: number): Promise<CertificateApplication> {
        try {
            const response = await apiClient.get(`/api/certificate-applications/${id}?populate=*`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching application:', error);
            throw error;
        }
    }

    async updateApplication(documentId: string, updateData: any): Promise<CertificateApplication> {
        try {
            const response = await apiClient.put(`/api/certificate-applications/${documentId}`, {
                data: updateData
            });
            return response.data.data;
        } catch (error) {
            console.error('Error updating application:', error);
            throw error;
        }
    }

    async deleteApplication(documentId: string): Promise<void> {
        try {
            await apiClient.delete(`/api/certificate-applications/${documentId}`);
        } catch (error) {
            console.error('Error deleting application:', error);
            throw error;
        }
    }

    async submitApplication(documentId: string): Promise<CertificateApplication> {
        try {
            console.log('🚀 Submitting application:', documentId);

            const response = await apiClient.put(`/api/certificate-applications/${documentId}`, {
                data: {
                    applicationStatus: 'submitted', // ✅ Chuyển từ draft → submitted
                    applicationDate: new Date().toISOString() // Update submission time
                }
            });

            console.log('✅ Application submitted successfully:', response.data.data.applicationNumber);
            return response.data.data;
        } catch (error) {
            console.error('❌ Error submitting application:', error);
            throw error;
        }
    }

    // ✅ New method to move application to review
    async startReviewApplication(documentId: string): Promise<CertificateApplication> {
        try {
            const response = await apiClient.put(`/api/certificate-applications/${documentId}`, {
                data: {
                    applicationStatus: 'under_review', // ✅ submitted → under_review
                    reviewedBy: localStorage.getItem('userId')
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error starting review:', error);
            throw error;
        }
    }

    // ✅ Method to request additional info
    async requestAdditionalInfo(documentId: string, message: string): Promise<CertificateApplication> {
        try {
            const response = await apiClient.put(`/api/certificate-applications/${documentId}`, {
                data: {
                    applicationStatus: 'additional_info_required', // ✅ any → additional_info_required
                    reviewNotes: message,
                    reviewedBy: localStorage.getItem('userId')
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error requesting additional info:', error);
            throw error;
        }
    }

    // ===== PENDING APPLICATIONS (ADMIN) =====

    async getPendingApplications(params: {
        page?: number;
        pageSize?: number;
        certificateType?: CertificateType;
    } = {}): Promise<APIResponse<CertificateApplication[]>> {
        try {
            console.log('🔍 Getting pending applications with params:', params);

            const queryParams = new URLSearchParams();

            // ✅ FIX: Pagination params
            if (params.page) queryParams.append('pagination[page]', params.page.toString());
            if (params.pageSize) queryParams.append('pagination[pageSize]', params.pageSize.toString());

            // ✅ FIX: Certificate type filter
            if (params.certificateType) {
                queryParams.append('filters[targetCertificateType][$eq]', params.certificateType);
            }

            // ✅ FIX: Status filter - Strapi v5 format
            // Thay vì multiple $in, sử dụng format đúng cho Strapi v5
            queryParams.append('filters[applicationStatus][$in][0]', 'submitted');
            queryParams.append('filters[applicationStatus][$in][1]', 'under_review');
            queryParams.append('filters[applicationStatus][$in][2]', 'additional_info_required');

            // Populate related data
            queryParams.append('populate[0]', 'applicant');
            queryParams.append('populate[1]', 'content');
            queryParams.append('populate[2]', 'reviewedBy');
            queryParams.append('populate[3]', 'supportingDocuments');

            // Sort by creation date
            queryParams.append('sort[0]', 'createdAt:desc');

            const url = `/api/certificate-applications?${queryParams.toString()}`;
            console.log('🌐 API URL:', url);

            const response = await apiClient.get(url);

            console.log('📊 API Response:', response.data);

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ Error fetching pending applications:', error);

            // ✅ DEBUG: Log detailed error info
            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);
                console.error('Response headers:', error.response.headers);
            }

            throw error;
        }
    }
    async getAllApplications(): Promise<APIResponse<CertificateApplication[]>> {
        try {
            console.log('🔍 Getting ALL applications for debug...');

            const queryParams = new URLSearchParams();
            queryParams.append('pagination[pageSize]', '100');
            queryParams.append('populate[0]', 'applicant');
            queryParams.append('populate[1]', 'content');
            queryParams.append('sort[0]', 'createdAt:desc');

            const url = `/api/certificate-applications?${queryParams.toString()}`;
            console.log('🌐 Debug API URL:', url);

            const response = await apiClient.get(url);

            console.log('📊 All Applications Response:', response.data);
            console.log('📊 Total applications:', response.data.meta?.pagination?.total);

            // Log each application's status
            if (response.data.data && Array.isArray(response.data.data)) {
                response.data.data.forEach((app: any, index: number) => {
                    console.log(`📋 App ${index + 1}:`, {
                        id: app.id,
                        documentId: app.documentId,
                        applicationNumber: app.applicationNumber,
                        applicationStatus: app.applicationStatus,
                        targetCertificateType: app.targetCertificateType,
                        createdAt: app.createdAt
                    });
                });
            }

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ Error fetching all applications:', error);
            throw error;
        }
    }

    /**
     * 🔥 FIXED: Approve application với đầy đủ params
     * 
     * @param documentId - Application document ID (Strapi v5 uses documentId)
     * @param approvalData - Dữ liệu phê duyệt
     * @returns Promise containing approved application and created certificate
     */
    async approveApplication(
        documentId: string, 
        approvalData: {
            reviewNotes?: string;
            reviewedBy?: number;
            applicationData?: any;  // 🔥 Chứa wipoNumber
            supportingDocuments?: number[];
            certificateFile?: number;
        }
    ): Promise<{
        application: CertificateApplication;
        certificate?: Certificate;
    }> {
        try {
            console.log('🚀 Starting approval process for application:', documentId);
            console.log('📝 Approval data:', approvalData);
    
            // ✅ STEP 1: Fetch application
            const applicationResponse = await apiClient.get(`/api/certificate-applications/${documentId}?populate=*`);
            const currentApplication = applicationResponse.data.data;
    
            console.log('📋 Current application:', {
                id: currentApplication.id,
                documentId: currentApplication.documentId,
                applicationNumber: currentApplication.applicationNumber,
                status: currentApplication.applicationStatus,
                certificateType: currentApplication.targetCertificateType
            });
    
            // ✅ STEP 2: Update application status
            const userId = approvalData.reviewedBy || (getUserIdFromToken() ? parseInt(getUserIdFromToken()) : null);
    
            // 🔥 Merge applicationData với data mới (bao gồm wipoNumber)
            const mergedApplicationData = {
                ...currentApplication.applicationData,
                ...approvalData.applicationData
            };
    
            const updateData: any = {
                applicationStatus: 'approved',
                reviewNotes: approvalData.reviewNotes || 'Đã xem xét và phê duyệt',
                reviewedBy: userId,
                applicationData: mergedApplicationData  // ← Lưu data mới vào applicationData
            };
    
            // ✅ NEW: Add certificate file if provided (NOIP/Association)
            if (approvalData.certificateFile) {
                updateData.certificateFile = approvalData.certificateFile;
                console.log('📄 Service: Added certificate file to approval:', approvalData.certificateFile);
            }
    
            console.log('📝 Update data for application:', updateData);
    
            const appUpdateResponse = await apiClient.put(`/api/certificate-applications/${documentId}`, {
                data: updateData
            });
    
            const approvedApplication = appUpdateResponse.data.data;
            console.log('✅ Application status updated to approved');
    
            // ✅ STEP 3: Update content status if exists
            if (currentApplication.content?.id) {
                try {
                    await apiClient.put(`/api/contents/${currentApplication.content.id}`, {
                        data: {
                            contentStatus: 'approved',
                            approvedAt: new Date().toISOString()
                        }
                    });
                    console.log('✅ Content status updated');
                } catch (contentError: any) {
                    console.warn('⚠️ Content status update failed (non-critical):', contentError);
                }
            }
    
            // ✅ STEP 4: Create certificate với wipoNumber từ applicationData
            console.log('📍 STEP 4: Creating certificate...');
    
            // 🔥 Lấy wipoNumber từ applicationData
            const wipoNumber = mergedApplicationData.wipoNumber;
    
            const certificateMetadata: any = {
                applicationId: currentApplication.id,
                applicationNumber: currentApplication.applicationNumber,
                applicationType: currentApplication.applicationType,
                processingFee: currentApplication.processingFee,
                approvedAt: new Date().toISOString(),
                approvedBy: userId,
                ...mergedApplicationData
            };
    
            const certificateData: any = {
                certificateNumber: this.generateCertificateNumber(currentApplication.targetCertificateType),
                certificateType: currentApplication.targetCertificateType,
                issuer: 'Vietrro Digital Rights Platform',
                issueDate: new Date().toISOString(),
                expiryDate: this.calculateExpiryDate(currentApplication.targetCertificateType),
                certificateStatus: 'active',
                verificationCode: this.generateVerificationCode(),
                content: currentApplication.content?.id || null,
                applicant: currentApplication.applicant?.id,
                metadata: certificateMetadata,
                issuedBy: userId,
                approvedBy: userId
            };
    
            // 🔥 Add wipoNumber nếu có (cho Vietrro-WIPO certificates)
            if (wipoNumber && currentApplication.targetCertificateType === 'vietrro') {
                certificateData.wipoNumber = wipoNumber;
                console.log('📝 Added WIPO Number to certificate:', wipoNumber);
            }
    
            // ✅ Add certificateFile for NOIP/Association
            if (approvalData.certificateFile) {
                certificateData.certificateFile = approvalData.certificateFile;
                console.log('📄 Added certificate file:', approvalData.certificateFile);
            }

            console.log('📋 Certificate data to create:', {
                certificateNumber: certificateData.certificateNumber,
                certificateType: certificateData.certificateType,
                wipoNumber: certificateData.wipoNumber,
                hasContent: !!certificateData.content
            });
    
            const certificateResponse = await apiClient.post('/api/certificates', {
                data: certificateData
            });
    
            if (!certificateResponse.data?.data) {
                throw new Error('Certificate creation failed - no data in response');
            }
    
            const createdCertificate = certificateResponse.data.data;
            console.log('✅ Certificate created successfully:', {
                id: createdCertificate.id,
                certificateNumber: createdCertificate.certificateNumber,
                wipoNumber: createdCertificate.wipoNumber,
                status: createdCertificate.certificateStatus,
                hasCertificateFile: !!createdCertificate.certificateFile
            });
    
            // ✅ STEP 5: Link certificate back to application
            try {
                await apiClient.put(`/api/certificate-applications/${documentId}`, {
                    data: {
                        resultingCertificate: createdCertificate.id
                    }
                });
                console.log('✅ Certificate linked to application');
            } catch (linkError) {
                console.warn('⚠️ Failed to link certificate (non-critical):', linkError);
            }
    
            // ✅ Final result
            const result = {
                application: approvedApplication,
                certificate: createdCertificate
            };
    
            console.log('🎉 Approval process completed successfully!');
            return result;
    
        } catch (error: any) {
            console.error('💥 Approval process failed:', error);
    
            if (error.response) {
                console.error('API Error:', {
                    status: error.response.status,
                    data: error.response.data,
                    url: error.response.config?.url
                });
            }
    
            throw new Error(`Approval failed: ${error.message}`);
        }
    }

    async rejectApplication(documentId: string, rejectionReason: string): Promise<CertificateApplication> {
        try {
            // Option 1: If you have custom endpoint
            // const response = await apiClient.post(`/api/certificate-applications/${documentId}/reject`, {
            //     rejectionReason
            // });
            // return response.data.data;

            // Option 2: Update using standard endpoint
            const response = await apiClient.put(`/api/certificate-applications/${documentId}`, {
                data: {
                    applicationStatus: 'rejected',
                    rejectionReason: rejectionReason,
                    reviewedBy: localStorage.getItem('userId')
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error rejecting application:', error);
            throw error;
        }
    }

    /**
     * ✅ NEW: Upload supporting documents (giấy phép để xem xét)
     * @param files - Array of files to upload
     * @returns Array of uploaded file IDs
     */
    async uploadSupportingDocuments(files: File[]): Promise<number[]> {
        try {
            console.log('📤 Service: Uploading supporting documents...', {
                fileCount: files.length,
                files: files.map(f => ({ name: f.name, size: f.size }))
            });

            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            const response = await apiClient.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const fileIds = response.data.map((file: any) => file.id);
            console.log('✅ Service: Supporting documents uploaded:', fileIds);

            return fileIds;
        } catch (error) {
            console.error('❌ Service: Error uploading supporting documents:', error);
            throw error;
        }
    }

    /**
     * ✅ NEW: Upload certificate file (file chứng nhận cuối cùng)
     * For NOIP/Association certificates only
     * @param file - Certificate PDF file
     * @returns Uploaded file ID
     */
    async uploadCertificateFile(file: File): Promise<number> {
        try {
            console.log('📤 Service: Uploading certificate file...', {
                name: file.name,
                size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                type: file.type
            });

            // Validate PDF
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                throw new Error('Certificate file must be PDF format');
            }

            // Validate size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                throw new Error('Certificate file must be less than 10MB');
            }

            const formData = new FormData();
            formData.append('files', file);

            const response = await apiClient.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const fileId = response.data[0].id;
            console.log('✅ Service: Certificate file uploaded:', fileId);

            return fileId;
        } catch (error) {
            console.error('❌ Service: Error uploading certificate file:', error);
            throw error;
        }
    }


    // ===== CERTIFICATE METHODS =====
    async getUserCertificates(params: {
        page?: number;
        pageSize?: number;
    } & CertificateSearchFilters = {}): Promise<APIResponse<Certificate[]>> {
        try {
            const queryParams = new URLSearchParams();

            // Pagination
            if (params.page) queryParams.append('pagination[page]', params.page.toString());
            if (params.pageSize) queryParams.append('pagination[pageSize]', params.pageSize.toString());

            // Filters
            if (params?.status?.length) {
                params.status.forEach((s, index) => {
                    queryParams.append(`filters[certificateStatus][$in][${index}]`, s);
                });
            }
            if (params?.type?.length) {
                params.type.forEach((t, index) => {
                    queryParams.append(`filters[certificateType][$in][${index}]`, t);
                });
            }
            // if (params.userId) {
            //     queryParams.append('filters[issuedBy][id][$eq]', params.userId.toString());
            // }

            // Add user filter
            // const userId = localStorage.getItem('userId');
            // if (userId) {
            //     queryParams.append('filters[issuedBy][id][$eq]', userId);
            // }

            // ✅ SỬ DỤNG UTILITY FUNCTION
            const canViewAll = canViewAllCertificates();

            if (canViewAll && !params.userId) {
                // Admin/Staff/Legal: Xem tất cả certificates
                // Không thêm user filter
            } else {
                const userId = params.userId ? params.userId : getUserIdFromToken();
                
                // Right_owner: Chỉ xem certificates của mình
                //queryParams.append('filters[owner][$eq]', userId);
                queryParams.append('filters[applicant][$eq]', userId.toString());
                // hoặc filters[applicant][$eq] tùy theo schema
            }

            // Sử dụng populate đơn giản cho Strapi v5
            queryParams.append('populate[0]', 'content');
            queryParams.append('populate[1]', 'issuedBy');
            queryParams.append('populate[2]', 'qrCode');
            queryParams.append('populate[3]', 'certificateFile');

            // Sort
            queryParams.append('sort[0]', 'createdAt:desc');
            
            const response = await apiClient.get(`/api/certificates?${queryParams.toString()}`);

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('Error fetching user certificates:', error);
            throw error;
        }
    }
    async getCertificateById(id: number): Promise<Certificate> {
        try {
            const response = await apiClient.get(`/api/certificates/${id}?populate=*`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching certificate:', error);
            throw error;
        }
    }

    async updateCertificate(documentId: string, updateData: any): Promise<Certificate> {
        try {
            const response = await apiClient.put(`/api/certificates/${documentId}`, {
                data: updateData
            });
            return response.data.data;
        } catch (error) {
            console.error('Error updating certificate:', error);
            throw error;
        }
    }

    async deleteCertificate(documentId: string): Promise<void> {
        try {
            await apiClient.delete(`/api/certificates/${documentId}`);
        } catch (error) {
            console.error('Error deleting certificate:', error);
            throw error;
        }
    }

    async revokeCertificate(documentId: string, reason: string): Promise<Certificate> {
        try {
            // Option 1: If you have custom endpoint
            // const response = await apiClient.post(`/api/certificates/${documentId}/revoke`, {
            //     reason
            // });
            // return response.data.data;

            // Option 2: Update status using standard Strapi endpoint
            const response = await apiClient.put(`/api/certificates/${documentId}`, {
                data: {
                    certificateStatus: 'revoked',
                    revocationReason: reason,
                    revocationDate: new Date().toISOString()
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error revoking certificate:', error);
            throw error;
        }
    }

    async renewCertificate(documentId: string): Promise<CertificateApplication> {
        try {
            // Option 1: If you have custom endpoint
            // const response = await apiClient.post(`/api/certificates/${documentId}/renew`);
            // return response.data.data;

            // Option 2: Get certificate info and create new application
            const certResponse = await apiClient.get(`/api/certificates/${documentId}?populate=*`);
            const certificate = certResponse.data.data;

            // Create renewal application
            const renewalData = {
                applicationNumber: this.generateApplicationNumber(),
                applicationType: 'renewal',
                applicationDate: new Date().toISOString(),
                applicationStatus: 'draft',
                targetCertificateType: certificate.certificateType,
                applicationData: {
                    workTitle: certificate.content?.title || '',
                    workType: certificate.certificateType,
                    creators: [],
                    description: certificate.content?.description || '',
                    firstPublicationDate: certificate.issueDate,
                    registrationTerritory: certificate.metadata?.territorialCoverage || ['VN'],
                    previousRegistrations: [{
                        organization: certificate.issuer,
                        registrationNumber: certificate.certificateNumber,
                        registrationDate: certificate.issueDate
                    }]
                },
                content: certificate.content?.id,
                priority: 'normal',
                paymentStatus: 'unpaid',
                processingFee: this.calculateProcessingFee(certificate.certificateType, 'renewal', 'normal'),
                estimatedProcessingTime: 15
            };

            const response = await apiClient.post('/api/certificate-applications', {
                data: renewalData
            });

            return response.data.data;
        } catch (error) {
            console.error('Error renewing certificate:', error);
            throw error;
        }
    }

    // async downloadCertificateOld(documentId: string): Promise<Blob> {
    //     try {
    //         const response = await apiClient.get(`/api/certificates/${documentId}/download`, {
    //             responseType: 'blob'
    //         });
    //         return response.data;
    //     } catch (error) {
    //         console.error('Error downloading certificate:', error);
    //         throw error;
    //     }
    // }

    async downloadCertificate(certificateFileUrl: string): Promise<Blob> {
        try {
            if(!certificateFileUrl) {
                throw new Error('Certificate không tồn tại');
            }

            const fileUrl = certificateFileUrl.startsWith('http') ? certificateFileUrl : `${apiClient.client.defaults.baseURL}${certificateFileUrl}`;
            const fileResponse = await apiClient.get(fileUrl, {
                responseType: 'blob'
            });            
            return fileResponse.data;
        } catch (error) {
            console.error('❌ Error downloading certificate:', error);
            throw error;
        }
    }

    // ===== VERIFICATION METHODS =====

    async verifyCertificate(certificateNumber: string, includeCode?: boolean): Promise<{
        certificate: Certificate | null;
        verification: CertificateVerification | null;
    }> {
        try {
            // First, find certificate by certificateNumber
            const params: any = {
                populate: '*',
            };

            if (includeCode) {
                // Filter OR: certificateNumber OR verificationCode
                params['filters[$or][0][certificateNumber][$eq]'] = certificateNumber;
                params['filters[$or][1][verificationCode][$eq]'] = certificateNumber;
            } else {
                // Default behavior
                params['filters[certificateNumber][$eq]'] = certificateNumber;
            }

            // Request certificate
            const certResponse = await apiClient.get('/api/certificates', {
                params,
            });
            
            // const certResponse = await apiClient.get('/api/certificates', {
            //     params: {
            //         'filters[certificateNumber][$eq]': certificateNumber,
            //         'populate': '*'
            //     }
            // });

            if (!certResponse.data.data || certResponse.data.data.length === 0) {
                return {
                    certificate: null,
                    verification: null
                };
            }

            const certificate = certResponse.data.data[0];

            // Create verification record
            const verificationData = {
                verificationDate: new Date().toISOString(),
                verificationMethod: 'verification_code',
                verificationResult: certificate.certificateStatus === 'active' ? 'valid' : 'invalid',
                certificate: certificate.id
            };

            const verifyResponse = await apiClient.post('/api/certificate-verifications', {
                data: verificationData
            });

            return {
                certificate: certificate,
                verification: verifyResponse.data.data
            };
        } catch (error) {
            console.error('Error verifying certificate:', error);
            throw error;
        }
    }

    async verifyCertificateByQR(qrData: string): Promise<{
        certificate: Certificate | null;
        verification: CertificateVerification | null;
    }> {
        try {
            const response = await apiClient.post('/api/certificates/verify-qr', {
                qrData
            });
            return response.data;
        } catch (error) {
            console.error('Error verifying certificate by QR:', error);
            throw error;
        }
    }

    // ===== TEMPLATE METHODS =====

    async getTemplates(certificateType?: CertificateType): Promise<CertificateTemplate[]> {
        try {
            const queryParams = new URLSearchParams();

            if (certificateType) {
                queryParams.append('filters[templateType][$eq]', certificateType);
            }
            queryParams.append('filters[isActive][$eq]', 'true');
            queryParams.append('populate', '*');
            queryParams.append('sort', 'version:desc');

            const response = await apiClient.get(`/api/certificate-templates?${queryParams.toString()}`);
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    }

    async getTemplateById(id: number): Promise<CertificateTemplate> {
        try {
            const response = await apiClient.get(`/api/certificate-templates/${id}?populate=*`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching template:', error);
            throw error;
        }
    }

    async createTemplate(templateData: any): Promise<CertificateTemplate> {
        try {
            const response = await apiClient.post('/api/certificate-templates', {
                data: templateData
            });
            return response.data.data;
        } catch (error) {
            console.error('Error creating template:', error);
            throw error;
        }
    }

    async updateTemplate(documentId: string, updateData: any): Promise<CertificateTemplate> {
        try {
            const response = await apiClient.put(`/api/certificate-templates/${documentId}`, {
                data: updateData
            });
            return response.data.data;
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    }

    async deleteTemplate(documentId: string): Promise<void> {
        try {
            await apiClient.delete(`/api/certificate-templates/${documentId}`);
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    }

    // ===== STATISTICS METHODS =====

    async getStatistics(): Promise<LicenseStats> {
        try {
            // Get applications count
            const applicationsResponse = await apiClient.get('/api/certificate-applications/count');
            const certificatesResponse = await apiClient.get('/api/certificates/count');

            // Get by status
            const pendingApps = await apiClient.get('/api/certificate-applications?filters[applicationStatus][$in]=submitted&filters[applicationStatus][$in]=under_review');
            const activeCerts = await apiClient.get('/api/certificates?filters[certificateStatus][$eq]=active');

            return {
                totalApplications: applicationsResponse.data || 0,
                pendingApplications: pendingApps.data.meta?.pagination?.total || 0,
                approvedApplications: 0, // Calculate from data
                rejectedApplications: 0, // Calculate from data
                activeCertificates: activeCerts.data.meta?.pagination?.total || 0,
                expiredCertificates: 0, // Calculate from data
                revokedCertificates: 0, // Calculate from data
                monthlyApplications: [],
                certificatesByType: [],
                averageProcessingTime: {
                    overall: 15,
                    byType: {
                        vietrro: 7,
                        wipo: 30,
                        noip: 20,
                        association: 15
                    },
                    byPriority: {
                        normal: 15,
                        urgent: 7,
                        express: 3
                    }
                }
            };
        } catch (error) {
            console.error('Error fetching statistics:', error);

            // Return mock data
            return {
                totalApplications: 0,
                pendingApplications: 0,
                approvedApplications: 0,
                rejectedApplications: 0,
                activeCertificates: 0,
                expiredCertificates: 0,
                revokedCertificates: 0,
                monthlyApplications: [],
                certificatesByType: [],
                averageProcessingTime: {
                    overall: 15,
                    byType: {
                        vietrro: 7,
                        wipo: 30,
                        noip: 20,
                        association: 15
                    },
                    byPriority: {
                        normal: 15,
                        urgent: 7,
                        express: 3
                    }
                }
            };
        }
    }

    async getDashboardStats(): Promise<any> {
        try {
            const response = await apiClient.get('/api/certificates/dashboard');
            return response.data;
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    }

    // ===== EXTERNAL INTEGRATION METHODS =====

    async registerWithWIPO(documentId: string, wipoData: any): Promise<ExternalRegistrationResult> {
        try {
            const response = await apiClient.post(`/api/certificate-applications/${documentId}/register-wipo`, wipoData);
            return response.data;
        } catch (error) {
            console.error('Error registering with WIPO:', error);
            throw error;
        }
    }

    async registerWithNOIP(documentId: string, noipData: any): Promise<ExternalRegistrationResult> {
        try {
            const response = await apiClient.post(`/api/certificate-applications/${documentId}/register-noip`, noipData);
            return response.data;
        } catch (error) {
            console.error('Error registering with NOIP:', error);
            throw error;
        }
    }

    async registerWithAssociation(
        documentId: string,
        associationId: string,
        associationData: any
    ): Promise<ExternalRegistrationResult> {
        try {
            const response = await apiClient.post(
                `/api/certificate-applications/${documentId}/register-association/${associationId}`,
                associationData
            );
            return response.data;
        } catch (error) {
            console.error('Error registering with association:', error);
            throw error;
        }
    }

    async getSupportedAssociations(): Promise<SupportedAssociation[]> {
        try {
            const response = await apiClient.get('/api/supported-associations?populate=*');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching supported associations:', error);

            // Return mock data
            return [
                {
                    id: 'vcpmc',
                    name: 'Trung tâm Bảo vệ Quyền tác giả Âm nhạc Việt Nam (VCPMC)',
                    type: 'music',
                    description: 'Tổ chức thu phí và phân phối tiền bản quyền cho các tác giả, nhà xuất bản âm nhạc',
                    isActive: true,
                    supportedcontentKinds: ['music', 'audio'],
                    processingFees: {
                        new: 500000,
                        renewal: 300000,
                        transfer: 200000,
                        amendment: 150000
                    },
                    estimatedProcessingTime: { normal: 15, urgent: 7, express: 3 }
                },
                {
                    id: 'vpa',
                    name: 'Hiệp hội Nhiếp ảnh Việt Nam (VPA)',
                    type: 'photography',
                    description: 'Tổ chức bảo vệ quyền tác giả nhiếp ảnh',
                    isActive: true,
                    supportedcontentKinds: ['image'],
                    processingFees: {
                        new: 300000,
                        renewal: 200000,
                        transfer: 150000,
                        amendment: 100000
                    },
                    estimatedProcessingTime: { normal: 10, urgent: 5, express: 2 }
                },
                {
                    id: 'vwa',
                    name: 'Hội Nhà văn Việt Nam (VWA)',
                    type: 'literature',
                    description: 'Tổ chức bảo vệ quyền tác giả văn học',
                    isActive: true,
                    supportedcontentKinds: ['text'],
                    processingFees: {
                        new: 400000,
                        renewal: 250000,
                        transfer: 180000,
                        amendment: 120000
                    },
                    estimatedProcessingTime: { normal: 12, urgent: 6, express: 3 }
                },
                {
                    id: 'vfda',
                    name: 'Hiệp hội Điện ảnh Việt Nam (VFDA)',
                    type: 'film',
                    description: 'Tổ chức quản lý bản quyền phim ảnh',
                    isActive: true,
                    supportedcontentKinds: ['video'],
                    processingFees: {
                        new: 800000,
                        renewal: 500000,
                        transfer: 350000,
                        amendment: 200000
                    },
                    estimatedProcessingTime: { normal: 20, urgent: 10, express: 5 }
                }
            ];
        }
    }

    async checkExternalStatus(externalReference: string): Promise<any> {
        try {
            const response = await apiClient.get(`/api/external-registrations/${externalReference}/status`);
            return response.data;
        } catch (error) {
            console.error('Error checking external status:', error);
            throw error;
        }
    }

    // ===== HELPER METHODS =====

    async uploadDocument(file: File, applicationId?: number): Promise<any> {
        try {
            const formData = new FormData();
            formData.append('files', file);

            if (applicationId) {
                formData.append('ref', 'api::certificate-application.certificate-application');
                formData.append('refId', applicationId.toString());
                formData.append('field', 'supportingDocuments');
            }

            const response = await apiClient.post('/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data[0];
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    }

    async deleteDocument(fileId: number): Promise<void> {
        try {
            await apiClient.delete(`/api/upload/files/${fileId}`);
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    }

    // ===== EXPORT METHODS =====

    async exportApplications(filters: ApplicationSearchFilters, format: 'csv' | 'pdf' | 'excel'): Promise<Blob> {
        try {
            const queryParams = new URLSearchParams();

            // Add filters
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    queryParams.append(key, String(value));
                }
            });

            queryParams.append('format', format);

            const response = await apiClient.get(`/api/certificate-applications/export?${queryParams.toString()}`, {
                responseType: 'blob'
            });

            return response.data;
        } catch (error) {
            console.error('Error exporting applications:', error);
            throw error;
        }
    }

    async exportCertificates(filters: CertificateSearchFilters, format: 'csv' | 'pdf' | 'excel'): Promise<Blob> {
        try {
            const queryParams = new URLSearchParams();

            // Add filters
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    queryParams.append(key, String(value));
                }
            });

            queryParams.append('format', format);

            const response = await apiClient.get(`/api/certificates/export?${queryParams.toString()}`, {
                responseType: 'blob'
            });

            return response.data;
        } catch (error) {
            console.error('Error exporting certificates:', error);
            throw error;
        }
    }

    // Private helper methods
    private generateApplicationNumber(): string {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 10000);
        return `APP-${timestamp}-${random}`;
    }

    private generateCertificateNumber(): string {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 10000);
        return `CERT-${timestamp}-${random}`;
    }

    private generateVerificationCode(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 12; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    }

    private calculateExpiryDate(certificateType: string): string {
        const now = new Date();
        const expiryYears = {
            vietrro: 1,
            wipo: 5,
            noip: 3,
            association: 1
        };

        const years = expiryYears[certificateType] || 1;
        now.setFullYear(now.getFullYear() + years);
        return now.toISOString();
    }

    private calculateProcessingFee(
        certificateType: string,
        applicationType: string,
        priority: string
    ): number {
        const baseFees: Record<string, Record<string, number>> = {
            vietrro: { new: 1000000, renewal: 500000, transfer: 750000, amendment: 300000 },
            wipo: { new: 5000000, renewal: 3000000, transfer: 4000000, amendment: 1500000 },
            noip: { new: 2000000, renewal: 1000000, transfer: 1500000, amendment: 500000 },
            association: { new: 500000, renewal: 300000, transfer: 400000, amendment: 200000 }
        };

        const priorityMultipliers: Record<string, number> = {
            normal: 1,
            urgent: 1.5,
            express: 2
        };

        const baseFee = baseFees[certificateType]?.[applicationType] || 1000000;
        const multiplier = priorityMultipliers[priority] || 1;

        return Math.round(baseFee * multiplier);
    }

    private getEstimatedProcessingTime(certificateType: string, priority: string): number {
        const baseTimes: Record<string, Record<string, number>> = {
            vietrro: { normal: 7, urgent: 3, express: 1 },
            wipo: { normal: 30, urgent: 15, express: 7 },
            noip: { normal: 20, urgent: 10, express: 5 },
            association: { normal: 15, urgent: 7, express: 3 }
        };

        return baseTimes[certificateType]?.[priority] || 15;
    }

    /**
     * THÊM METHOD: Enhanced getUserCertificates với tab support
     * SỬ DỤNG existing getUserCertificates logic nhưng thêm certificateType filtering
     */
    async getUserCertificatesByType(
        certificateType: 'all' | CertificateType,
        params: {
            page?: number;
            pageSize?: number;
            status?: string;
            search?: string;
        } = {}
    ): Promise<APIResponse<Certificate[]>> {
        try {
            // SỬ DỤNG existing logic từ getUserCertificates
            const queryParams = new URLSearchParams();

            // Pagination - GIỐNG existing
            if (params.page) queryParams.append('pagination[page]', params.page.toString());
            if (params.pageSize) queryParams.append('pagination[pageSize]', params.pageSize.toString());

            // THÊM Certificate type filter
            if (certificateType !== 'all') {
                queryParams.append('filters[certificateType][$eq]', certificateType);
            }

            // Status filters - GIỐNG existing
            if (params.status && params.status !== 'all') {
                queryParams.append('filters[certificateStatus][$eq]', params.status);
            }

            // Search filter - THÊM
            if (params.search) {
                queryParams.append('filters[$or][0][certificateNumber][$containsi]', params.search);
                queryParams.append('filters[$or][1][metadata.workTitle][$containsi]', params.search);
                queryParams.append('filters[$or][2][content.title][$containsi]', params.search);
            }

            // User permission logic - SỬ DỤNG existing pattern từ getUserCertificates
            const userId = getUserIdFromToken();
            const canViewAll = canViewAllCertificates();

            if (canViewAll) {
                // Admin/Staff/Legal: Xem tất cả certificates - GIỐNG existing
            } else {
                // Right_owner: Chỉ xem certificates của mình - GIỐNG existing
                queryParams.append('filters[applicant][$eq]', userId);
            }

            // Populate - SỬ DỤNG existing populate pattern
            queryParams.append('populate[0]', 'content');
            queryParams.append('populate[1]', 'applicant');
            queryParams.append('populate[2]', 'qrCode');
            queryParams.append('populate[3]', 'certificateFile');

            // Sort - GIỐNG existing
            queryParams.append('sort[0]', 'createdAt:desc');

            const response = await apiClient.get(`/api/certificates?${queryParams.toString()}`);

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('Error fetching certificates by type:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Get certificate với enhanced populate
     * SỬ DỤNG existing getCertificateById logic nhưng thêm populate options
     */
    async getCertificateByIdEnhanced(id: number, includeContent: boolean = true): Promise<Certificate> {
        try {
            let populateQuery = '?populate=*'; // Default như existing

            if (includeContent) {
                // Enhanced populate cho content details
                populateQuery = '?' +
                    'populate[0]=content&' +
                    'populate[1]=applicant&' +
                    'populate[2]=qrCode&' +
                    'populate[3]=certificateFile&' +
                    'populate[4]=issuedBy&' +
                    'populate[5]=approvedBy';
            }

            const response = await apiClient.get(`/api/certificates/${id}${populateQuery}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching certificate by ID enhanced:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Search certificates với advanced options
     * SỬ DỤNG existing query patterns
     */
    async searchCertificatesAdvanced(searchOptions: {
        query?: string;
        certificateTypes?: CertificateType[];
        statuses?: string[];
        dateFrom?: string;
        dateTo?: string;
        page?: number;
        pageSize?: number;
    }): Promise<APIResponse<Certificate[]>> {
        try {
            const queryParams = new URLSearchParams();

            // User permission - SỬ DỤNG existing pattern
            //const userId = getUserIdFromToken();
            //const canViewAll = canViewAllCertificates();

            // if (!canViewAll) {
            //     queryParams.append('filters[applicant][$eq]', userId);
            // }

            // Text search
            if (searchOptions.query) {
                queryParams.append('filters[$or][0][certificateNumber][$containsi]', searchOptions.query);
                queryParams.append('filters[$or][1][metadata.workTitle][$containsi]', searchOptions.query);
                queryParams.append('filters[$or][2][content.title][$containsi]', searchOptions.query);
                queryParams.append('filters[$or][3][content.description][$containsi]', searchOptions.query);
            }

            // Certificate type filter
            if (searchOptions.certificateTypes && searchOptions.certificateTypes.length > 0) {
                searchOptions.certificateTypes.forEach((type, index) => {
                    queryParams.append(`filters[certificateType][$in][${index}]`, type);
                });
            }

            // Status filter
            if (searchOptions.statuses && searchOptions.statuses.length > 0) {
                searchOptions.statuses.forEach((status, index) => {
                    queryParams.append(`filters[certificateStatus][$in][${index}]`, status);
                });
            }

            // Date range filters
            if (searchOptions.dateFrom) {
                queryParams.append('filters[issueDate][$gte]', searchOptions.dateFrom);
            }
            if (searchOptions.dateTo) {
                queryParams.append('filters[issueDate][$lte]', searchOptions.dateTo);
            }

            // Pagination - GIỐNG existing
            queryParams.append('pagination[page]', (searchOptions.page || 1).toString());
            queryParams.append('pagination[pageSize]', (searchOptions.pageSize || 20).toString());

            // Populate - SỬ DỤNG existing pattern
            queryParams.append('populate[0]', 'content');
            queryParams.append('populate[1]', 'applicant');
            queryParams.append('populate[2]', 'qrCode');
            queryParams.append('populate[3]', 'certificateFile');

            // Sort - GIỐNG existing
            queryParams.append('sort[0]', 'createdAt:desc');

            const response = await apiClient.get(`/api/certificates?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error in advanced certificate search:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Get statistics by certificate type
     * SỬ DỤNG existing getStatistics pattern
     */
    async getStatisticsByType(certificateType?: 'all' | CertificateType): Promise<LicenseStats> {
        try {
            // Có thể call API endpoint riêng nếu có
            const response = await apiClient.get(`/api/certificates/statistics${certificateType && certificateType !== 'all' ? `?type=${certificateType}` : ''}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching statistics by type:', error);

            // Fallback: SỬ DỤNG existing getStatistics và filter client-side
            try {
                const allStats = await this.getStatistics();

                // Nếu cần filter by type, có thể làm client-side processing ở đây
                // Hoặc return existing stats structure
                return allStats;
            } catch (fallbackError) {
                // Return existing mock structure từ getStatistics
                return {
                    totalApplications: 0,
                    pendingApplications: 0,
                    approvedApplications: 0,
                    rejectedApplications: 0,
                    activeCertificates: 0,
                    expiredCertificates: 0,
                    revokedCertificates: 0,
                    monthlyApplications: [],
                    certificatesByType: [],
                    averageProcessingTime: {
                        overall: 15,
                        byType: {
                            vietrro: 7,
                            wipo: 30,
                            noip: 20,
                            association: 15
                        },
                        byPriority: {
                            normal: 15,
                            urgent: 7,
                            express: 3
                        }
                    }
                };
            }
        }
    }

    /**
     * THÊM METHOD: Enhanced download với better filename
     * SỬ DỤNG existing downloadCertificate logic
     */
    async downloadCertificateEnhanced(documentId: string, customFilename?: string): Promise<Blob> {
        try {
            // SỬ DỤNG existing downloadCertificate method
            const response = await apiClient.get(`/api/certificates/${documentId}/download`, {
                responseType: 'blob',
                // Có thể thêm custom headers nếu cần
                headers: customFilename ? { 'X-Custom-Filename': customFilename } : {}
            });
            return response.data;
        } catch (error) {
            console.error('Error downloading certificate enhanced:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Bulk download certificates
     * SỬ DỤNG existing permission patterns
     */
    async bulkDownloadCertificates(certificateIds: number[]): Promise<Blob> {
        try {
            const userId = getUserIdFromToken();

            const response = await apiClient.post('/api/certificates/bulk-download', {
                certificateIds,
                requestedBy: userId // Cho permission checking
            }, {
                responseType: 'blob'
            });

            return response.data;
        } catch (error) {
            console.error('Error bulk downloading certificates:', error);

            // Fallback: Download individually và zip client-side
            // Hoặc throw error để handle ở store level
            throw error;
        }
    }

    /**
     * THÊM METHOD: Get trending certificates
     * SỬ DỤNG existing query patterns
     */
    async getTrendingCertificates(certificateType?: CertificateType, limit: number = 10): Promise<Certificate[]> {
        try {
            const queryParams = new URLSearchParams();

            // Type filter
            if (certificateType) {
                queryParams.append('filters[certificateType][$eq]', certificateType);
            }

            // Only active certificates
            queryParams.append('filters[certificateStatus][$eq]', 'active');

            // Pagination
            queryParams.append('pagination[pageSize]', limit.toString());

            // Sort by creation date (fallback vì chưa có view/download metrics)
            queryParams.append('sort[0]', 'createdAt:desc');

            // Basic populate - SỬ DỤNG existing pattern
            queryParams.append('populate[0]', 'content');
            queryParams.append('populate[1]', 'applicant');

            const response = await apiClient.get(`/api/certificates?${queryParams.toString()}`);
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching trending certificates:', error);

            // Fallback: Return empty array thay vì throw
            return [];
        }
    }

    /**
     * THÊM METHOD: Export certificates với existing permission logic
     * SỬ DỤNG existing exportCertificates pattern nếu có
     */
    async exportCertificatesEnhanced(
        format: 'csv' | 'excel' | 'pdf',
        filters: {
            certificateType?: 'all' | CertificateType;
            status?: string;
            dateFrom?: string;
            dateTo?: string;
        } = {}
    ): Promise<Blob> {
        try {
            const queryParams = new URLSearchParams();

            // Format
            queryParams.append('format', format);

            // User permission - SỬ DỤNG existing pattern
            const userId = getUserIdFromToken();
            const canViewAll = canViewAllCertificates();

            if (!canViewAll) {
                queryParams.append('userId', userId);
            }

            // Filters
            if (filters.certificateType && filters.certificateType !== 'all') {
                queryParams.append('certificateType', filters.certificateType);
            }
            if (filters.status && filters.status !== 'all') {
                queryParams.append('status', filters.status);
            }
            if (filters.dateFrom) {
                queryParams.append('dateFrom', filters.dateFrom);
            }
            if (filters.dateTo) {
                queryParams.append('dateTo', filters.dateTo);
            }

            const response = await apiClient.get(`/api/certificates/export?${queryParams.toString()}`, {
                responseType: 'blob'
            });

            return response.data;
        } catch (error) {
            console.error('Error exporting certificates:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Enhanced verification với more details
     * SỬ DỤNG existing verifyCertificate logic
     */
    async verifyCertificateEnhanced(certificateNumber: string): Promise<{
        certificate: Certificate | null;
        verification: CertificateVerification | null;
        additionalInfo: {
            isExpiringSoon: boolean;
            daysUntilExpiry: number | null;
            verificationHistory: number;
        };
    }> {
        try {
            // SỬ DỤNG existing verifyCertificate method
            const result = await this.verifyCertificate(certificateNumber);

            // Thêm enhanced info
            let additionalInfo = {
                isExpiringSoon: false,
                daysUntilExpiry: null as number | null,
                verificationHistory: 0
            };

            if (result.certificate) {
                // Check if expiring soon
                if (result.certificate.expiryDate) {
                    const expiryDate = new Date(result.certificate.expiryDate);
                    const now = new Date();
                    const diffTime = expiryDate.getTime() - now.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    additionalInfo.daysUntilExpiry = diffDays;
                    additionalInfo.isExpiringSoon = diffDays <= 30 && diffDays > 0;
                }

                // Count verification history (có thể expand sau)
                additionalInfo.verificationHistory = 1; // Current verification
            }

            return {
                ...result,
                additionalInfo
            };
        } catch (error) {
            console.error('Error in enhanced certificate verification:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Get certificate analytics
     * SỬ DỤNG existing patterns cho API calls
     */
    async getCertificateAnalytics(certificateId: number): Promise<{
        viewStats: Array<{ date: string; views: number }>;
        downloadStats: Array<{ date: string; downloads: number }>;
        verificationStats: Array<{ date: string; verifications: number }>;
    }> {
        try {
            const response = await apiClient.get(`/api/certificates/${certificateId}/analytics`);
            return response.data;
        } catch (error) {
            console.error('Error fetching certificate analytics:', error);

            // Fallback: Return mock data structure
            return {
                viewStats: [],
                downloadStats: [],
                verificationStats: []
            };
        }
    }

    /**
     * THÊM METHOD: Generate QR code for certificate
     * SỬ DỤNG existing patterns
     */
    async generateCertificateQRCode(certificateId: number, size: number = 256): Promise<string> {
        try {
            // Check if certificate already has QR code
            const certificate = await this.getCertificateById(certificateId);

            if (certificate.qrCode?.url) {
                return certificate.qrCode.url;
            }

            // Generate new QR code
            const response = await apiClient.post(`/api/certificates/${certificateId}/generate-qr`, {
                size
            });

            return response.data.qrCodeUrl || '';
        } catch (error) {
            console.error('Error generating certificate QR code:', error);

            // Fallback: Generate verification URL
            const verificationUrl = `${window.location.origin}/certificates/verify/${certificateId}`;
            return verificationUrl;
        }
    }

    /**
     * THÊM METHOD: Enhanced certificate renewal
     * SỬ DỤNG existing renewCertificate logic nhưng với better handling
     */
    async renewCertificateEnhanced(documentId: string, renewalOptions?: {
        extendPeriod?: number; // months
        updateMetadata?: boolean;
        notes?: string;
    }): Promise<CertificateApplication> {
        try {
            // Get certificate info - SỬ DỤNG existing pattern
            const certResponse = await apiClient.get(`/api/certificates/${documentId}?populate=*`);
            const certificate = certResponse.data.data;

            // Create renewal application - SỬ DỤNG existing generateApplicationNumber
            const renewalData = {
                applicationNumber: this.generateApplicationNumber(),
                applicationType: 'renewal',
                applicationDate: new Date().toISOString(),
                applicationStatus: 'draft',
                targetCertificateType: certificate.certificateType,
                applicationData: {
                    workTitle: certificate.metadata?.workTitle || certificate.content?.title || '',
                    workType: certificate.certificateType,
                    creators: certificate.metadata?.creators || [],
                    description: certificate.content?.description || '',
                    firstPublicationDate: certificate.issueDate,
                    registrationTerritory: certificate.metadata?.territorialCoverage || ['VN'],
                    previousRegistrations: [{
                        organization: certificate.issuer,
                        registrationNumber: certificate.certificateNumber,
                        registrationDate: certificate.issueDate
                    }],
                    // Enhanced: Add renewal notes
                    renewalNotes: renewalOptions?.notes || 'Certificate renewal request'
                },
                content: certificate.content?.id,
                priority: 'normal',
                paymentStatus: 'unpaid',
                processingFee: this.calculateProcessingFee(certificate.certificateType, 'renewal', 'normal'),
                estimatedProcessingTime: 15,
                // Enhanced: Add renewal-specific data
                originalCertificate: certificate.id,
                requestedExtensionPeriod: renewalOptions?.extendPeriod || 12
            };

            const response = await apiClient.post('/api/certificate-applications', {
                data: renewalData
            });

            return response.data.data;
        } catch (error) {
            console.error('Error renewing certificate enhanced:', error);
            throw error;
        }
    }

    /**
     * THÊM METHOD: Certificate sharing
     * Tương thích với existing patterns
     */
    async shareCertificate(certificateId: number, shareOptions: {
        method: 'email' | 'link' | 'qr';
        recipients?: string[];
        message?: string;
        expiresIn?: number; // hours
    }): Promise<{
        shareUrl: string;
        shareId: string;
        expiresAt?: string;
    }> {
        try {
            const response = await apiClient.post(`/api/certificates/${certificateId}/share`, shareOptions);
            return response.data;
        } catch (error) {
            console.error('Error sharing certificate:', error);

            // Fallback: Generate simple share URL
            const shareUrl = `${window.location.origin}/certificates/public/${certificateId}`;
            return {
                shareUrl,
                shareId: `share_${Date.now()}`,
                expiresAt: shareOptions.expiresIn
                    ? new Date(Date.now() + shareOptions.expiresIn * 60 * 60 * 1000).toISOString()
                    : undefined
            };
        }
    }
    /**
 * Submit supplementary documents for certificate application
 */
    async submitSupplementaryDocuments(
        documentId: string,
        files: File[],
        notes?: string
    ): Promise<CertificateApplication> {
        try {
            console.log('📤 Submitting supplementary documents for:', documentId);

            const formData = new FormData();

            // Append files
            files.forEach(file => {
                formData.append('files', file);
            });

            // Append notes if provided
            if (notes) {
                formData.append('notes', notes);
            }

            // ✅ Dùng apiClient như các method khác
            const response = await apiClient.post(
                `/api/certificate-applications/${documentId}/supplementary-documents`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            console.log('✅ Supplementary documents submitted successfully');
            return response.data.data;
        } catch (error) {
            console.error('❌ Error submitting supplementary documents:', error);
            throw error;
        }
    }

    /**
 * Get supplementary documents for certificate application
 */
    async getSupplementaryDocuments(documentId: string): Promise<any[]> {
        try {
            console.log('📄 Getting supplementary documents for:', documentId);

            const response = await apiClient.get(
                `/api/certificate-applications/${documentId}/supplementary-documents`
            );

            console.log('✅ Retrieved supplementary documents');
            return response.data.data || [];
        } catch (error) {
            console.error('❌ Error fetching supplementary documents:', error);
            throw error;
        }
    }

    /**
 * Delete a supplementary document
 */
    async deleteSupplementaryDocument(
        documentId: string,
        fileId: number
    ): Promise<CertificateApplication> {
        try {
            console.log('🗑️ Deleting supplementary document:', fileId);

            const response = await apiClient.delete(
                `/api/certificate-applications/${documentId}/supplementary-documents/${fileId}`
            );

            console.log('✅ Document deleted successfully');
            return response.data.data;
        } catch (error) {
            console.error('❌ Error deleting supplementary document:', error);
            throw error;
        }
    }

    /**
     * Update supplementary notes
     */
    async updateSupplementaryNotes(
        documentId: string,
        notes: string
    ): Promise<CertificateApplication> {
        try {
            console.log('📝 Updating supplementary notes');

            const response = await apiClient.put(
                `/api/certificate-applications/${documentId}/supplementary-notes`,
                { notes }
            );

            console.log('✅ Notes updated successfully');
            return response.data.data;
        } catch (error) {
            console.error('❌ Error updating notes:', error);
            throw error;
        }
    }
}

export default new CertificateService();
